import { DeviceTransmissionService } from '@core/transmission/DeviceTransmissionService'
import { Action, TransmissionState, NoticeType as NoticeActionType } from '@core/databus/IMessage'
import { AbsDownloadTask } from '@/core/transmission/AbsDownloadTask'
import { ILLEGAL_FILE_SIZE } from './AbsTask'
import type { GroupFileInfo, MirrorTaskInfo, DeviceTaskError } from './ITask'
import { TaskError, TaskState, TransmissionTaskType } from './ITask'
import { TransmissionDataBusProxy } from './TransmissionDataBusProxy'
import { STATUS_CODE_NATIVE_SERVICE_BIND_FAILED } from '@/netadapters/EndpointAdapter'
import type { TransmissionNoticeData } from '../databus/DataSyncManager'
import { generateDebounceNoticeSender } from '@/core/Notification/NotificationManager'

const DEFAULT_COMPRESSION_PATH = '/storage/emulated/0/Download/OWork/'
const COMPRESSION_NOTICE_TAG = 'COMPRESSION_NOTICE_LIST'

export const readCompressionNotice = (id: string) => {
  const compression_notice_list: string[] = JSON.parse(sessionStorage.getItem(COMPRESSION_NOTICE_TAG) || '[]')

  sessionStorage.setItem(
    COMPRESSION_NOTICE_TAG,
    JSON.stringify(compression_notice_list.filter((taskId) => taskId !== id))
  )
}

export class DownloadTaskZip extends AbsDownloadTask {
  protected _transmissionTaskType: TransmissionTaskType = TransmissionTaskType.ZIP
  #groupFileInfo: GroupFileInfo
  #targetPath?: string
  #targetZipSize?: number
  #showErrorNotice = generateDebounceNoticeSender(
    {
      msg: `压缩文件失败了,原因${this.failedReason}`,
      action: Action.VIEW_TRANSMISSION,
      type: NoticeActionType.COMMON_ACTION,
      extraQuery: `taskId=${this.id}`,
    },
    COMPRESSION_NOTICE_TAG,
    (switchIdentifier: string): boolean => {
      const compression_notice_list: string[] = JSON.parse(sessionStorage.getItem(switchIdentifier) || '[]')

      const isPrompted = compression_notice_list.includes(this.id)

      !isPrompted && compression_notice_list.push(this.id)
      sessionStorage.setItem(switchIdentifier, JSON.stringify(compression_notice_list))

      return isPrompted
    }
  )

  constructor(deviceId: string, groupFileInfo: GroupFileInfo, appId: string, isViaCloud: boolean) {
    super(
      deviceId,
      {
        fileName: groupFileInfo.fileName,
        fileFormat: 'zip',
        fileType: 'zip',
        fileSize: 0,
        parentPath: DEFAULT_COMPRESSION_PATH,
      },
      !isViaCloud,
      appId
    )
    this.#groupFileInfo = groupFileInfo
  }

  public copy(): MirrorTaskInfo | undefined {
    if (this.#targetPath && this.#targetZipSize) {
      return {
        appId: this._appId,
        deviceId: this._deviceId,
        fileInfos: [
          {
            fileName: this._fileName,
            fileSize: this.#targetZipSize,
            fileType: this._fileType,
            fileFormat: this._fileFormat,
            parentPath: this.#targetPath,
          },
        ],
        isViaCloud: !this._isViaLan,
        id: this.id,
        sequenceId: this.sequenceId,
        sourceTaskType: this._transmissionTaskType,
      }
    }
  }

  async start(): Promise<void> {
    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return
    }

    try {
      const notice = await TransmissionDataBusProxy.getInstance().appendNotice({
        appId: this._appId,
        isUpload: false,
        targetDevice: this._deviceId,
        fileName: this._fileName,
        fileSize: ILLEGAL_FILE_SIZE,
        transmitted: ILLEGAL_FILE_SIZE,
        state: TransmissionState.ON_GOING,
        businessType: TransmissionTaskType.ZIP,
      })

      console.log(`[Transmission] zip ${new Date().toLocaleString()} `, 'notice:', notice)

      if (!notice.data) {
        this._failedReason = TaskError.CREATE_NOTICE_FAILED
        this._handleError(undefined)

        return
      }
      this._preTransmissionNotice = JSON.stringify(notice.data)
      this._noticeId = notice.data.id
      const DeviceTransmissionResponse = await DeviceTransmissionService.compress(
        device,
        this.#groupFileInfo,
        this.noticeId
      )

      console.log(
        `[Transmission] ${new Date().toLocaleString()}  fileName:`,
        this._fileName,
        'DeviceTransmissionResponse:zip',
        DeviceTransmissionResponse
      )

      if (DeviceTransmissionResponse.isSuccess) {
        console.log(`[Transmission] zip task ${new Date().toLocaleString()} 发起一个压缩任务:`, notice)

        this._isInDeviceSideExecutingStage = true
        // this._checkLatestStatus()
        this._taskScheduler?.updateTask(this)
      } else {
        if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
          this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
        } else {
          this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
        }

        this._handleError(undefined)
      }
    } catch (error) {
      console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:zip', error)
      this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED

      this._handleError(error)
    }
  }

  async resume(): Promise<void> {
    // do not exist in this version
    // if (!this._isInDeviceSideExecutingStage && isEmpty(this._downloadUrl)) {
    //   this.start()
    //   return
    // }
    // const device = this._getDevice()
    // if (!device) {
    //   this._terminateDueToDeviceNotFound()
    //   return
    // }
    // try {
    //   // 服务id待定
    //   const DeviceTransmissionResponse = await DeviceTransmissionService.resume(
    //     device,
    //     this._noticeId,
    //     false,
    //     TransmissionTaskType.ZIP
    //   )
    //   console.log(
    //     `[Transmission] zip ${new Date().toLocaleString()}  fileName:`,
    //     this._fileName,
    //     'DeviceTransmissionResponse:resume',
    //     DeviceTransmissionResponse
    //   )
    //   if (!DeviceTransmissionResponse.isSuccess) {
    //     if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
    //       this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
    //     } else {
    //       this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
    //     }
    //     this._handleError(undefined)
    //   } else {
    //     this._checkLatestStatus()
    //   }
    // } catch (error) {
    //   console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:resume', error)
    //   this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
    //   this._handleError(error)
    // }
  }

  async onRemove(needCallService?: boolean): Promise<boolean> {
    this.markAsDeleted()

    if (!this._isInDeviceSideExecutingStage) {
      this._requestCancelToken?.cancel()
      this._requestCancelToken = undefined

      return true
    }
    if (!needCallService) return true
    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return true
    }

    try {
      const DeviceTransmissionResponse = await DeviceTransmissionService.cancel(
        device,
        this._noticeId,
        false,
        TransmissionTaskType.ZIP
      )

      console.log(
        `[Transmission] ${new Date().toLocaleString()}  fileName:`,
        this._fileName,
        'DeviceTransmissionResponse:cancel',
        DeviceTransmissionResponse
      )

      if (!DeviceTransmissionResponse.isSuccess) {
        if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
          this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
        } else {
          this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
        }

        this._handleError(undefined)

        return true
      }

      return true
    } catch (error) {
      console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:cancel', error)
      this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED

      this._handleError(error)

      return true
    }
  }

  /**
   * 根据通知消息更新任务状态
   * @param transmissionNotice
   * @param isFreezed
   * @returns
   */
  updateInfo(transmissionNotice: TransmissionNoticeData, isFreezed?: boolean): void {
    const { state, fileSize, transmitted, targetPath, targetZipSize, reason } = transmissionNotice

    // const { fileSize, transmitted, targetPath, targetZipSize } = transmissionNotice
    // const state = TransmissionState.DONE_FAILED,
    //   reason = DeviceTaskError.COMMON
    console.log(`[Transmission] zip task ${new Date().toLocaleString()} 触发本地任务更新:`, transmissionNotice)
    if ([TaskState.SUCCESS, TaskState.FAILED, TaskState.PAUSE].includes(this.state)) return

    switch (state) {
      case TransmissionState.DONE_SUCCEED:
        this._fileSize = fileSize
        this._transmitted = transmitted
        this.#targetZipSize = targetZipSize
        this._fileDownloadListener?.onProgressChanged?.(this._transmitted, this._fileSize, this._deviceId, true)

        if (typeof targetPath === 'string' && targetPath !== 'null') {
          this.#targetPath = targetPath.substring(0, targetPath.lastIndexOf('/') + 1)

          this._completionRatio = 100

          this._taskScheduler?.updateTask(this)
          this._fileDownloadListener?.onSuccess(this._deviceId, targetPath, this._fileName, this)
        } else {
          this._failedReason = TaskError.DEVICE_NOTIFIED_FAILURE_MESSAGE
          this._handleError(undefined)
        }
        this._checkLatestStatus.cancel()
        this.clearTimer()

        break
      case TransmissionState.DONE_FAILED:
        console.log(
          `[Transmission] ${new Date().toLocaleString()}  fileName:`,
          this._fileName,
          'device notifies  task has failed:',
          state
        )

        this._failedReason = reason as DeviceTaskError
        this._handleError(undefined)
        this.clearTimer()
        this._checkLatestStatus.cancel()
        break
      case TransmissionState.PAUSE:
        this.clearTimer()
        this._checkLatestStatus.cancel()
        break
      case TransmissionState.ON_GOING:
        if (this._state === TaskState.FAILED || this._state === TaskState.SUCCESS) return

        this._checkLatestStatus()
        if (fileSize === ILLEGAL_FILE_SIZE || transmitted === ILLEGAL_FILE_SIZE) return
        this._fileSize = fileSize
        this._transmitted = transmitted > this._transmitted ? transmitted : this._transmitted
        this._completionRatio = fileSize === 0 ? 0 : (this._transmitted / this._fileSize) * 100
        this._taskScheduler?.updateTask(this, isFreezed)
        console.log(
          `[Transmission] zip task ${new Date().toLocaleString()} ${this._fileName} 压缩任务进度更新:`,
          this._transmitted,
          this._fileSize,
          this._completionRatio
        )

        this._fileDownloadListener?.onProgressChanged?.(this._transmitted, this._fileSize, this._deviceId, true)

        break
      default:
        break
    }
  }

  protected _handleError(error: unknown): void {
    super._handleError(error)
    // if (this.isDeleted) return
    // this.#showErrorNotice()
  }
}
